import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiEye, FiEyeOff, FiMail, FiLock, FiLoader, FiShield } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../assets/images/logo.png';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const from = location.state?.from?.pathname || '/';

  // Cargar credenciales guardadas
  useEffect(() => {
    const saved = localStorage.getItem('betel_remember');
    if (saved) {
      try {
        const { username, password } = JSON.parse(saved);
        setFormData({ username, password });
        setRememberMe(true);
      } catch (e) {
        localStorage.removeItem('betel_remember');
      }
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error al escribir
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      setError('Por favor ingresa usuario y contraseña');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await login(formData);

      if (result.success) {
        // Guardar o limpiar credenciales según checkbox
        if (rememberMe) {
          localStorage.setItem('betel_remember', JSON.stringify({
            username: formData.username,
            password: formData.password
          }));
        } else {
          localStorage.removeItem('betel_remember');
        }

        // Redirigir al portal si es un cliente, al dashboard administrativo si es staff
        const redirectTo = result.user?.role === 'CLIENT' ? '/portal' : '/system';
        navigate(redirectTo, { replace: true });
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Error inesperado. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          {/* Header con logo principal */}
          <div className="login-header">
            <div className="logo-container">
              <img 
                src={logo} 
                alt="Betel Natural" 
                className="main-logo"
              />
            </div>
            
            <div className="login-description">
              <h1 className="login-title">Inicio de sesión más rápido y seguro en tu cuenta de Betel Natural</h1>
              <p className="login-subtitle">
                La próxima vez recordaremos como te gusta iniciar sesión de manera que puedas omitir el ingreso de un correo. 
                Siempre podrás regresar al inicio de sesión clásico.
              </p>
            </div>
          </div>

          {/* Contenido en dos columnas */}
          <div className="login-content">
            {/* Columna izquierda - Formulario */}
            <div className="login-form-column">
              <form onSubmit={handleSubmit} className="login-form">
                {error && (
                  <div className="error-alert">
                    <div className="error-message">{error}</div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="username" className="form-label">
                    Correo electrónico
                  </label>
                  <div className="input-wrapper">
                    <FiMail className="input-icon" />
                    <input
                      id="username"
                      type="email"
                      className="form-input"
                      placeholder="tu@email.com"
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      disabled={loading}
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="password" className="form-label">
                    Contraseña
                  </label>
                  <div className="input-wrapper">
                    <FiLock className="input-icon" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className="form-input"
                      placeholder="Ingresa tu contraseña"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      disabled={loading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={toggleShowPassword}
                      disabled={loading}
                      tabIndex="-1"
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: '4px', marginBottom: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-secondary, #555)' }}>
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loading}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    Recordar usuario y contraseña
                  </label>
                </div>

                <button
                  type="submit"
                  className="login-button"
                  disabled={loading || !formData.username || !formData.password}
                >
                  {loading ? (
                    <>
                      <FiLoader className="spin" />
                      Iniciando sesión...
                    </>
                  ) : (
                    'Iniciar Sesión'
                  )}
                </button>
              </form>

              {/* Footer del formulario */}
              <div className="login-footer">
                <div className="security-info">
                  <div className="security-item">
                    <FiShield className="security-icon" />
                    <span>Conexión segura SSL</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Columna derecha - Información */}
            <div className="login-info-column">
              <div className="info-section">
                <h3>Sistema Administrativo </h3>
                <div className="feature-list">
                  <div className="feature-item">
                    <div className="feature-icon">🏢</div>
                    <div className="feature-text">
                      <h4>Gestión Empresarial</h4>
                      <p>Control total de clientes, inventario y operaciones</p>
                    </div>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon">📊</div>
                    <div className="feature-text">
                      <h4>Reportes Inteligentes</h4>
                      <p>Análisis de ventas y métricas en tiempo real</p>
                    </div>
                  </div>
                  <div className="feature-item">
                    <div className="feature-icon">🚀</div>
                    <div className="feature-text">
                      <h4>Automatización</h4>
                      <p>Procesos optimizados para mayor eficiencia</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Background decorativo */}
      <div className="login-background">
        <div className="bg-shape bg-shape-1"></div>
        <div className="bg-shape bg-shape-2"></div>
        <div className="bg-shape bg-shape-3"></div>
      </div>
    </div>
  );
};

export default Login;
